import React, { useEffect, useState } from 'react';
import FieldTypeSelector from '../investments/addInvestment/inputs/Index';
import {
  commonFieldConfig,
  commonFieldTypes,
} from '../investments/addInvestment/inputs/field.config';
import { AppDispatch, RootState } from '../../redux/store';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigate, useParams } from 'react-router-dom';
import GetByIdApi from '../../api/genericApis/GetById.api';
import UpdateApi from '../../api/genericApis/Update.api';
import Swal from 'sweetalert2';
import Skeleton from 'react-loading-skeleton'; // Import Skeleton
import { ModuleType } from '../../api/modules.types';
import {
  clearEditCompletedProjectsData,
  EditCompanyProjectsState,
  setEditCompletedProjectsData,
  updateEditCompletedProjectFieldValue,
} from '../../redux/completedProjects/editCompletedProjectsSlice';

export default function EditProject() {
  const dispatch: AppDispatch = useDispatch();
  const projectData = useSelector(
    (state: RootState) => state.editCompletedProjects,
  );
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(true); // Loading state

  const fetchData = async () => {
    if (!id) return;
    try {
      const data = (
        await GetByIdApi({ moduleType: ModuleType.project, id: +id })
      ).data as unknown as EditCompanyProjectsState;
      dispatch(setEditCompletedProjectsData({ projectData: data }));
      setLoading(false); // Data loaded, set loading to false
    } catch (error) {
      console.error('Error fetching data:', error);
      navigate('/projects');
    }
  };

  useEffect(() => {
    fetchData();
    return () => {
      dispatch(clearEditCompletedProjectsData()); // Clean up on unmount
    };
  }, [id]);

  // Handle input changes for fields
  const handleInputChange = ({
    e,
    field,
    lang,
    isSameValue,
  }: {
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    field: string;
    lang: 'ar' | 'en';
    isSameValue: boolean;
  }) => {
    const { value } = e.target;

    dispatch(
      updateEditCompletedProjectFieldValue({
        language: lang,
        field,
        value,
        isSameValue,
      }),
    );
  };

  const completedProjectFieldsEn = [
    {
      label: 'Project Name',
      value: projectData.en.projectName,
      field: 'projectName',
      config: { ...commonFieldConfig['en'][commonFieldTypes.text] },
    },
    {
      label: 'Project Description',
      value: projectData.en.projectDescription,
      field: 'projectDescription',
      config: { ...commonFieldConfig['en'][commonFieldTypes.text] },
    },
    {
      label: 'Project Location',
      value: projectData.en.projectLocation,
      field: 'projectLocation',
      config: { ...commonFieldConfig['en'][commonFieldTypes.dropdown] },
    },
  ];

  const completedProjectFieldsAr = [
    {
      label: 'اسم المشروع',
      value: projectData.ar.projectName,
      field: 'projectName',
      config: { ...commonFieldConfig['ar'][commonFieldTypes.text] },
    },
    {
      label: 'وصف المشروع',
      value: projectData.ar.projectDescription,
      field: 'projectDescription',
      config: { ...commonFieldConfig['ar'][commonFieldTypes.text] },
    },
    {
      label: 'موقع المشروع',
      value: projectData.ar.projectLocation,
      field: 'projectLocation',
      config: { ...commonFieldConfig['ar'][commonFieldTypes.text] },
    },
  ];

  const imageSection = {
    label: 'Project Image URL',
    value: projectData.en.projectImage,
    field: 'projectImage',
    config: { ...commonFieldConfig['en'][commonFieldTypes.image] },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Confirm with SweetAlert before submitting
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to submit the updated service data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, submit!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        UpdateApi({
          moduleType: ModuleType.project,
          id: Number(id),
          data: projectData,
        })
          .then(() => {
            // On success
            Swal.fire({
              title: 'Success!',
              text: 'Service has been successfully updated.',
              icon: 'success',
              confirmButtonText: 'Ok',
            });

            // Navigate to the service table page after success
            navigate('/projects');
          })
          .catch((error) => {
            // On error
            Swal.fire({
              title: 'Error!',
              text: 'There was an issue updating the service. Please try again.',
              icon: 'error',
              confirmButtonText: 'Ok',
            });
          });
      }
    });
  };

  return (
    <section className="mb-5 d-flex flex-column">
      <h3 className="mb-3">Edit Service</h3>

      <div className="row">
        <div className="col-md-6">
          {loading ? (
            <>
              <Skeleton height={24} width="25%" className="mb-3" />
              <Skeleton height={38} width="100%" className="mb-3" />
              <Skeleton height={24} width="25%" className="mb-3" />
              <Skeleton height={38} width="100%" className="mb-3" />
            </>
          ) : (
            completedProjectFieldsEn.map((field) => (
              <FieldTypeSelector
                fieldName={field.field}
                key={field.field}
                label={field.label}
                value={field.value}
                config={field.config}
                onInputChange={(e) =>
                  handleInputChange({
                    e,
                    field: field.field,
                    lang: 'en',
                    isSameValue: field.config.isSameValue,
                  })
                }
              />
            ))
          )}
        </div>

        <div className="col-md-6" dir="rtl">
          {loading ? (
            <>
              <Skeleton height={24} width="25%" className="mb-3" />
              <Skeleton height={38} width="100%" className="mb-3" />
              <Skeleton height={24} width="25%" className="mb-3" />
              <Skeleton height={38} width="100%" className="mb-3" />
            </>
          ) : (
            completedProjectFieldsAr.map((field) => (
              <FieldTypeSelector
                fieldName={field.field}
                label={field.label}
                value={field.value}
                config={field.config}
                onInputChange={(e) =>
                  handleInputChange({
                    e,
                    field: field.field,
                    lang: 'ar',
                    isSameValue: field.config.isSameValue,
                  })
                }
              />
            ))
          )}
        </div>
      </div>

      {loading ? (
        <div className="d-flex flex-wrap flex-md-nowrap justify-content-between align-items-center w-100">
          <div className="w-50">
            <Skeleton height={45} width={300} className="mb-3" />
          </div>

          <div className="d-flex justify-content-center w-100">
            <Skeleton height={150} width={200} className="mb-3" />
          </div>
        </div>
      ) : (
        imageSection && (
          <FieldTypeSelector
            fieldName={imageSection.field}
            label={imageSection.label}
            value={imageSection.value}
            config={imageSection.config}
            onInputChange={(e) =>
              handleInputChange({
                e,
                field: imageSection.field,
                lang: 'en',
                isSameValue: imageSection.config?.isSameValue,
              })
            }
          />
        )
      )}

      <button
        onClick={handleSubmit}
        className="btn btn-primary mt-4 align-self-center p-2"
        disabled={loading} // Disable submit button during loading
      >
        Submit
      </button>
    </section>
  );
}
